import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'
import type { LeagueSummary, StandingsEntry, TransferWindowState } from './types'

// ── Query key factories ───────────────────────────────────────────────────────

export const leagueKeys = {
  all:              ['leagues'] as const,
  list:             ()                                => [...leagueKeys.all, 'list']                              as const,
  detail:           (id: string)                     => [...leagueKeys.all, id]                                  as const,
  standings:        (id: string, gwId?: string)      => [...leagueKeys.detail(id), 'standings', gwId ?? 'latest'] as const,
  gameweekStatus:   (id: string)                     => [...leagueKeys.detail(id), 'gameweek-status']            as const,
  mostPickedPlayer: (id: string, gwId?: string)      => [...leagueKeys.detail(id), 'most-picked', gwId ?? 'latest'] as const,
  fixtures:         (id: string)                     => [...leagueKeys.detail(id), 'fixtures']                   as const,
}

export const competitionKeys = {
  all:  ['competitions'] as const,
  list: () => [...competitionKeys.all, 'list'] as const,
}

export const matchKeys = {
  all:        ['matches'] as const,
  byGameweek: (gameweekId: string) => [...matchKeys.all, 'gameweek', gameweekId] as const,
}

// ── Competitions ──────────────────────────────────────────────────────────────

export type Competition = {
  id:       string
  name:     string
  slug:     string
  sport:    'FOOTBALL' | 'CS2'
  isActive: boolean
  startsAt: string
  endsAt:   string
}

export async function fetchCompetitions(): Promise<Competition[]> {
  return apiClient.get<Competition[]>(endpoints.competitions.list())
}

export function useCompetitions() {
  return useQuery({
    queryKey: competitionKeys.list(),
    queryFn:  fetchCompetitions,
    staleTime: 10 * 60_000,
  })
}

// ── Leagues ───────────────────────────────────────────────────────────────────

export async function fetchUserLeagues(): Promise<LeagueSummary[]> {
  return apiClient.get<LeagueSummary[]>(endpoints.leagues.list())
}

// ── Mutation functions ────────────────────────────────────────────────────────

export type JoinLeagueResult = {
  membershipId: string
  leagueId:     string
  joinedAt:     string
}

export async function joinLeague(code: string, teamName?: string): Promise<JoinLeagueResult> {
  return apiClient.post<JoinLeagueResult>(endpoints.leagueMemberships.join(), { code, teamName })
}

export async function updateTeamName(leagueId: string, teamName: string | null): Promise<void> {
  await apiClient.patch<void>(endpoints.leagues.teamName(leagueId), { teamName })
}

// ── Favorites ─────────────────────────────────────────────────────────────────

export async function addFavoriteLeague(leagueId: string): Promise<void> {
  await apiClient.post<void>(endpoints.leagues.favorite(leagueId), {})
}

export async function removeFavoriteLeague(leagueId: string): Promise<void> {
  await apiClient.delete<void>(endpoints.leagues.favorite(leagueId))
}

// ── Create league ─────────────────────────────────────────────────────────────

export type CreateLeagueInput = {
  name:           string
  competitionId:  string
  format:         string
  transferOption: string
  teamName?:      string | undefined
}

export type CreateLeagueApiResult = {
  leagueId: string
  name:     string
  joinCode: string
}

export async function createLeague(input: CreateLeagueInput): Promise<CreateLeagueApiResult> {
  return apiClient.post<CreateLeagueApiResult>(endpoints.leagues.create(), input)
}

export async function deleteLeague(leagueId: string): Promise<void> {
  await apiClient.delete<void>(endpoints.leagues.delete(leagueId))
}

// ── League detail ─────────────────────────────────────────────────────────────

export type LeagueDetail = {
  id: string
  joinCode: string
  format: string
  transferOption: string
  memberCount: number
  competitionId: string
  isLocked: boolean   // true once the league's first gameweek has locked
}

export async function fetchLeagueDetail(leagueId: string): Promise<LeagueDetail> {
  return apiClient.get<LeagueDetail>(endpoints.leagues.detail(leagueId))
}

export function useLeagueDetail(leagueId: string): UseQueryResult<LeagueDetail> {
  return useQuery({
    queryKey: leagueKeys.detail(leagueId),
    queryFn:  () => fetchLeagueDetail(leagueId),
    staleTime: 5 * 60_000,
  })
}

// ── Gameweek status ───────────────────────────────────────────────────────────

export type GameweekSummary = {
  id: string
  number: number
  status: string
  scoresFinalized: boolean
}

export type LeagueGameweekStatus = {
  currentGameweekId: string
  currentGameweekNumber: number
  transferWindowState: TransferWindowState
  windowChangesAt: string | null
  gameweeks: GameweekSummary[]
}

export async function fetchLeagueGameweekStatus(leagueId: string): Promise<LeagueGameweekStatus> {
  return apiClient.get<LeagueGameweekStatus>(endpoints.leagueDetail.gameweekStatus(leagueId))
}

export function useLeagueGameweekStatus(leagueId: string) {
  return useQuery({
    queryKey: leagueKeys.gameweekStatus(leagueId),
    queryFn: () => fetchLeagueGameweekStatus(leagueId),
    staleTime: 5 * 60_000,
  })
}

// ── Standings by gameweek ─────────────────────────────────────────────────────

export async function fetchLeagueStandings(leagueId: string, gameweekId?: string): Promise<StandingsEntry[]> {
  const rows = await apiClient.get<StandingsEntry[]>(endpoints.leagueDetail.tableByGw(leagueId, gameweekId))
  // The API omits null fields (WhenWritingNull), so previousRank arrives as `undefined` for a
  // league's first scored gameweek. Normalize to null so the `=== null` guards in the UI hide
  // the rank-delta indicator instead of rendering "NaN posições".
  return rows.map(r => ({ ...r, previousRank: r.previousRank ?? null }))
}

export function useLeagueStandings(leagueId: string, gameweekId?: string) {
  return useQuery({
    queryKey: leagueKeys.standings(leagueId, gameweekId),
    queryFn: () => fetchLeagueStandings(leagueId, gameweekId),
    staleTime: 60_000,
  })
}

// ── Most picked player ────────────────────────────────────────────────────────

export type MostPickedPlayer = {
  playerId: string
  name: string
  position: string
  team: string
  pickCount: number
  pickPercentage: number
  price: number
  gwPoints: number | null
}

export function useMostPickedPlayer(
  leagueId: string,
  gameweekId?: string,
  windowState?: TransferWindowState,
) {
  return useQuery({
    queryKey: leagueKeys.mostPickedPlayer(leagueId, gameweekId),
    queryFn: () => apiClient.get<MostPickedPlayer | null>(
      endpoints.leagueDetail.mostPickedPlayer(leagueId, gameweekId),
    ),
    staleTime: 5 * 60_000,
    enabled: windowState === 'closed',
  })
}

// ── H2H Fixtures ──────────────────────────────────────────────────────────────

export type LeagueFixture = {
  gameweek: number
  homeUserId: string
  homeTeamName: string
  homePoints: number | null
  awayUserId: string | null
  awayTeamName: string
  awayPoints: number | null
  status: 'upcoming' | 'live' | 'finished'
  isCurrentUserMatch: boolean
}

export async function fetchLeagueFixtures(leagueId: string): Promise<LeagueFixture[]> {
  return apiClient.get<LeagueFixture[]>(endpoints.leagueDetail.fixtures(leagueId))
}

export function useLeagueFixtures(leagueId: string, enabled = true) {
  return useQuery({
    queryKey: leagueKeys.fixtures(leagueId),
    queryFn:  () => fetchLeagueFixtures(leagueId),
    staleTime: 60_000,
    enabled,
  })
}

// ── Gameweek matches (real-world fixtures of a round) ──────────────────────────
// GET /v1/gameweeks/{gameweekId}/matches — public competition schedule.
// Null fields are omitted by the API (WhenWritingNull), hence the optionals below.

export const MatchStatus = {
  Scheduled: 'SCHEDULED',
  Live:      'LIVE',
  Halftime:  'HALFTIME',
  Finished:  'FINISHED',
  Postponed: 'POSTPONED',
} as const
export type MatchStatus = (typeof MatchStatus)[keyof typeof MatchStatus]

export type MatchTeam = {
  id: string
  name: string
  code: string
  groupCode?: string    // "A".."H" for WC group stage; absent for knockouts / Brasileirão
  isEliminated: boolean
}

export type GameweekMatch = {
  id: string
  externalId: string
  status: MatchStatus
  kickoffAt: string             // ISO UTC
  homeTeam?: MatchTeam          // absent for unresolved knockout placeholders
  awayTeam?: MatchTeam
  homeTeamPlaceholder?: string  // e.g. "Vencedor do Jogo 34"
  awayTeamPlaceholder?: string
  homeScore?: number
  awayScore?: number
  isThirdPlaceMatch: boolean
}

export async function fetchGameweekMatches(gameweekId: string): Promise<GameweekMatch[]> {
  return apiClient.get<GameweekMatch[]>(endpoints.matches.list(gameweekId))
}

export function useGameweekMatches(gameweekId: string | undefined) {
  return useQuery({
    queryKey: matchKeys.byGameweek(gameweekId ?? 'none'),
    // queryFn only runs when `enabled` is true, i.e. gameweekId is defined.
    queryFn:  () => fetchGameweekMatches(gameweekId ?? ''),
    staleTime: 60_000,
    enabled:  !!gameweekId,
  })
}
